import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import i18n from "src/i18n/config";
import { useAppSettingsService } from "src/services/appSettingsService/appSettingsService";
import UserSettingsService from "src/services/userSettingsService";
import { RootState, useDispatch } from "src/store";

import { showDetails, updateSearchQuery } from "@dashboard/devices/store";
import { selectDevicesEntities } from "@dashboard/devices/store/selectors/devices";
import { selectGroupsEntities } from "@dashboard/groups/store/selectors";
import { selectLicensesEntities } from "@dashboard/licenses/store/selectors";
import { rolloutDeviceStatusLabel, rolloutDeviceStatusTooltip } from "@dashboard/rollouts/utils/rolloutStatus";
import { Typography } from "@mui/material";
import { createSelector } from "@reduxjs/toolkit";
import { DateCell } from "@shared/table/DateCell";
import { MinimalTableCell } from "@shared/table/MinimalTableComponents";
import { TextLink } from "@shared/TextLink";
import { Tooltip } from "@shared/Tooltip";
import { ColumnDef } from "@tanstack/react-table";

export type RolloutDeviceRow = API.RolloutDeviceReport & {
    licenseId: number | null;
    name: string;
    serial: string;
    groupId: number | null;
    group: string;
};

export const selectRolloutDeviceRows = createSelector(
    [
        selectDevicesEntities,
        selectLicensesEntities,
        selectGroupsEntities,
        (_state: RootState, reports: API.RolloutDeviceReport[]) => reports,
    ],
    (devices, licenses, groups, reports): RolloutDeviceRow[] =>
        reports.map((report) => {
            const device = devices[report.deviceId];
            const license = device ? licenses[device.licenseId] : undefined;
            return {
                ...report,
                licenseId: device?.licenseId ?? null,
                name: device?.name ?? "",
                serial: device?.serial ?? "",
                groupId: license?.groupId ?? null,
                group: (license && groups[license.groupId]?.name) ?? license?.groupName ?? "",
            };
        }),
);

const DeviceNameCell = ({ row }: { row: RolloutDeviceRow }) => {
    const { t } = useTranslation(["common", "sidepanels"]);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    if (row.licenseId === null) {
        return <MinimalTableCell>{t("sidepanels:rolloutDetailsPanel.deviceById", { deviceId: row.deviceId })}</MinimalTableCell>;
    }

    if (!row.name) {
        return <MinimalTableCell>-</MinimalTableCell>;
    }

    const licenseId = row.licenseId;
    const onClick = () => {
        UserSettingsService.setWorkspaceValue("devicePage.columnFilters", []);
        dispatch(updateSearchQuery(row.serial));
        dispatch(showDetails({ type: "license", selectedId: licenseId }));
        void navigate("/dashboard/devices");
    };

    return (
        <MinimalTableCell>
            <TextLink onClick={onClick} tooltip={t("common:tooltip.showDevices", { count: 1 })}>
                {row.name}
            </TextLink>
        </MinimalTableCell>
    );
};

const GroupNameCell = ({ row }: { row: RolloutDeviceRow }) => {
    const { t } = useTranslation(["common"]);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { effectiveWorkspaceSettings } = useAppSettingsService();

    if (!row.group) {
        return <MinimalTableCell>-</MinimalTableCell>;
    }

    if (row.groupId === null || !effectiveWorkspaceSettings.isGroupsPageEnabled) {
        return <MinimalTableCell>{row.group}</MinimalTableCell>;
    }

    const groupId = row.groupId;
    const onClick = () => {
        UserSettingsService.setWorkspaceValue("groupPage.columnFilters", []);
        UserSettingsService.setWorkspaceValue("groupPage.search", "");
        dispatch(showDetails({ type: "group", selectedId: groupId }));
        void navigate("/dashboard/groups");
    };

    return (
        <MinimalTableCell>
            <TextLink onClick={onClick} tooltip={t("common:tooltip.showGroupDetails")}>
                {row.group}
            </TextLink>
        </MinimalTableCell>
    );
};

const DeviceStatusCell = ({ status }: { status: API.RolloutDeviceStatus }) => (
    <MinimalTableCell>
        <Tooltip title={rolloutDeviceStatusTooltip[status]?.()}>
            <Typography>{rolloutDeviceStatusLabel[status]?.() ?? status}</Typography>
        </Tooltip>
    </MinimalTableCell>
);

export const columns: ColumnDef<RolloutDeviceRow>[] = [
    {
        id: "deviceid",
        header: i18n.t("common:label.device"),
        accessorKey: "deviceId",
        cell: (info) => <DeviceNameCell row={info.row.original} />,
    },
    {
        id: "group",
        header: i18n.t("common:label.group"),
        accessorKey: "group",
        enableSorting: false,
        cell: (info) => <GroupNameCell row={info.row.original} />,
    },
    {
        id: "status",
        header: i18n.t("common:label.status"),
        accessorKey: "status",
        cell: (info) => <DeviceStatusCell status={info.getValue<API.RolloutDeviceStatus>()} />,
    },
    {
        id: "updated",
        header: i18n.t("common:label.updated"),
        accessorKey: "updated",
        enableSorting: false,
        cell: (info) => <DateCell value={info.getValue<string>()} />,
    },
];
